import React, { useMemo, useState, useEffect } from "react";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import moment, { Moment } from "moment";
import CtaComponent from "../../../components/reusable-ui/cta";
import CModal from "../../../components/reusable-ui/Modal";
import DatePickerWrapper from "../../../components/reusable-ui/date-picker-wrapper";
import { INVENTORY_ACTIVITY_TYPES, INVENTORY_APPROVED_VALUES, ACTIVITY_DROPDOWN_STATUS, optionsDataForFilter, ACTIVITY_MODIFIED_STATUS, POST_FINAL_STAGE_STATUS } from "../data-handling/finance-inventory-constants";
import { toastError } from "../../../utils/react-toaster";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import useFinanceInventory from "../data-handling/use-finance-inventory";
import { convertArrayToObject } from "../../../helpers/common-helper";
import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";
import { allowEverything } from "../../../utils";

type ModalProps = {
  open: boolean;
  onClose: () => void;
};


function ActivityFilterModal({ open, onClose }: ModalProps) {
  const [approveStatus, setApproveStatus] = useState<any>("All");
  const [isEdited, setIsEdited] = useState<any>("All");
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const [sourceBranchIds, setSourceBranchIds] = useState<string[]>([]);
  const [supplierId, setSupplierId] = useState<any>("");
  const [postFinalFilter, setPostFinalFilter] = useState<string | undefined>(undefined);
  const [isPostFinalActivityEdited, setIsPostFinalActivityEdited] = useState<boolean>(false);
  const [selectedDropdownValue, setSelectedDropdownValue] = useState("");
  const { activityType, applyFilteredActivity, activityFilterObj } = useFinanceInventory();
  const [branchesDataMap, setBranchesDataMap] = useState<Record<string, any>>({});
  const { permittedInventoryBranches } = useSelector((state: RootState) => ({
    permittedInventoryBranches: state.businessReducer.permittedInventoryBranches || []
  }));
  const isPostFinalActivity = activityType === INVENTORY_ACTIVITY_TYPES.post_final_goods_received_note || activityType === INVENTORY_ACTIVITY_TYPES.post_final_transfer_ins
  const { inventorySupplierData } = useSelector((state: RootState) => ({
    inventorySupplierData: (state.inventoryReducer.inventorySupplierData || []).filter(
      (supplier: { type?: string }) => supplier.type === 'External'
    )
  }));

  useEffect(() => {
    if (activityType === INVENTORY_ACTIVITY_TYPES.post_final_goods_received_note ||
      activityType === INVENTORY_ACTIVITY_TYPES.post_final_transfer_ins
    ) {
          setApproveStatus(INVENTORY_APPROVED_VALUES.yes);
          setPostFinalFilter(POST_FINAL_STAGE_STATUS.approved);    }
  }, [activityType]);

  useEffect(() => {
    setSourceBranchIds(activityFilterObj?.sourceBranchIds ?? []);
    setSupplierId(activityFilterObj?.supplierId ?? "");
    setPostFinalFilter(activityFilterObj?.postFinalFilter);
    setIsPostFinalActivityEdited(activityFilterObj?.isPostFinalActivityEdited ?? false);
    setApproveStatus(
      activityFilterObj?.postFinalFilter === POST_FINAL_STAGE_STATUS.approved
        ? INVENTORY_APPROVED_VALUES.yes
        : activityFilterObj?.postFinalFilter === POST_FINAL_STAGE_STATUS.rejected
          ? INVENTORY_APPROVED_VALUES.no
          : activityFilterObj?.postFinalFilter === POST_FINAL_STAGE_STATUS.pending
            ? INVENTORY_APPROVED_VALUES.pending
            : INVENTORY_APPROVED_VALUES.all
    );
    setIsEdited(
      activityFilterObj?.isPostFinalActivityEdited === true
        ? INVENTORY_APPROVED_VALUES.yes
        : activityFilterObj?.isPostFinalActivityEdited === false
          ? INVENTORY_APPROVED_VALUES.no
          : INVENTORY_APPROVED_VALUES.all
    );
  }, [activityFilterObj]);


  const supplierDataMap = useMemo(() => {
    return convertArrayToObject(inventorySupplierData, "mid");
  }, [inventorySupplierData]);

  useEffect(() => {
    if (permittedInventoryBranches) {
      setBranchesDataMap(
        convertArrayToObject(permittedInventoryBranches, "mid")
      );
    }
  }, [permittedInventoryBranches]);

  const handleStatus = (val: any) => {
    setApproveStatus(val);
    if (val === INVENTORY_APPROVED_VALUES.yes) {
      setPostFinalFilter(POST_FINAL_STAGE_STATUS.approved);
    } else if (val === INVENTORY_APPROVED_VALUES.no) {
      setPostFinalFilter(POST_FINAL_STAGE_STATUS.rejected);
    } else if (val === INVENTORY_APPROVED_VALUES.pending) {
      setPostFinalFilter(POST_FINAL_STAGE_STATUS.pending);
    } else {
      setPostFinalFilter(undefined);
    }
  }

  const handleIsEdited = (val: any) => {
    setIsEdited(val);
    setIsPostFinalActivityEdited(val === INVENTORY_APPROVED_VALUES.yes)
  }

  const onDateRangeSubmission = (date: { startDate: moment.Moment; endDate: moment.Moment }) => {
    const maxAllowedDays = 31;
    const selectedDays = date.endDate.diff(date.startDate, "days");

    if (selectedDays > maxAllowedDays) {
      setFromDate(null);
      setToDate(null);
      toastError("Please select a date range of 31 days or less.");
      return;
    }
    setFromDate(date.startDate.format("YYYY-MM-DD"));
    setToDate(date.endDate.format("YYYY-MM-DD"));
  };

  const handleSourceSelection = (values: string[]) => {
    setSupplierId("");
    setSourceBranchIds(values);
  };

  const handleSupplierSelection = (values: string) => {
    setSourceBranchIds([]);
    setSupplierId(Array.isArray(values) ? values[0] : values);
  };

  const handleClearAllFilters = () => {
    const clearedFilters = {
      activityType: undefined,
      fromDate: null,
      toDate: null,
      sourceBranchIds: [],
      supplierId: ""
    };

   applyFilteredActivity?.(clearedFilters);
   onClose();

     setFromDate(null);
     setToDate(null);
  };

  const handleApplyFilters = () => {
    let filters: {
      activityType?: string;
      postFinalFilter?: string;
      isPostFinalActivityEdited?: boolean;
      fromDate?: string | null;
      toDate?: string | null;
      sourceBranchIds?: string[];
      supplierId?: string;
    } = {
      activityType: activityType ?? undefined,
      postFinalFilter,
      isPostFinalActivityEdited,
      fromDate,
      toDate,
      sourceBranchIds,
      supplierId
    };

    if (approveStatus === INVENTORY_APPROVED_VALUES.all) {
      delete filters.postFinalFilter;
    }
    if (isEdited === INVENTORY_APPROVED_VALUES.all) {
      delete filters.isPostFinalActivityEdited;
    }
    if (activityType !== INVENTORY_ACTIVITY_TYPES.post_final_goods_received_note &&
      activityType !== INVENTORY_ACTIVITY_TYPES.post_final_transfer_ins
    ) {
      delete filters.isPostFinalActivityEdited;
    }
    if (activityType === INVENTORY_ACTIVITY_TYPES.post_final_goods_received_note ||
      activityType === INVENTORY_ACTIVITY_TYPES.post_final_transfer_ins
    ) {
      filters.postFinalFilter = POST_FINAL_STAGE_STATUS.approved;
    }
    applyFilteredActivity?.(filters);
    onClose();
  };

  return (
    <CModal
      isOpen={open}

      className="p-6 w-full max-w-[850px]"
      body={
        <div className="relative w-full px-6 py-8 sm:px-4 sm:py-6">

          <div className="w-full">
            <div className="flex justify-end mb-4">
              <CtaComponent
                ctaText={"Clear All Filter"}
                ctaType={"secondary-active-cta"}
                ctaSize={CTA_SIZE.small}
                onCtaClick={handleClearAllFilters}
                customClass={"mr-12 border select-none cursor-pointer active:brightness-75 active:bg-slate-100 border-solid flex items-center text-13 px-12 py-10 border-[#E8E8E9] rounded-[8px] text-blue-500 !text-blue-500"}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8 mb-8">
              <div className={`w-full ${isPostFinalActivity ? "cursor-not-allowed" : ""}`}>
                <label htmlFor="Status" className="block text-sm font-medium text-gray-700 mb-16 pl-8">
                  Approval Stage
                </label>
                <CustomDropdown
                  optionsMap={ACTIVITY_DROPDOWN_STATUS || {}}
                  isMultiSelect={false}
                  placeholder={"Approval Stage"}
                  getListItemText={(data: any) => {
                    return data?.text;
                  }}
                  searchPlaceholder={""}
                  onSubmit={(value: any) =>
                    handleStatus(value[0])
                  }
                  preSelectedValues={approveStatus ?? undefined}
                  uniqueKey={"value"}
                  showErrorBox={false}
                  isDisabled={isPostFinalActivity}
                ></CustomDropdown>

              </div>

              {(activityType === INVENTORY_ACTIVITY_TYPES.transfer_ins ||
                activityType === INVENTORY_ACTIVITY_TYPES.post_final_transfer_ins
              ) ? (
                <div className="w-full">
                  <label htmlFor="SourceStore" className="mb-16 block text-sm font-medium text-gray-700 pl-8">
                    Source Store
                  </label>
                  <CustomDropdown
                    optionsMap={branchesDataMap || {}}
                    isMultiSelect={true}
                    placeholder={"Select Source"}
                    getListItemText={(data: any) => data?.branchName}
                    dropdownHeading={"Stores"}
                    searchPlaceholder={""}
                    onSubmit={(branchIds: any) =>
                      handleSourceSelection(branchIds)
                    }
                    regexCheck={allowEverything}
                    preSelectedValues={sourceBranchIds}
                    uniqueKey={"mid"}
                    showErrorBox={false}
                  ></CustomDropdown>
                </div>
              ) : (
                <div className="w-full">
                  <label htmlFor="Supplier" className="mb-16 block text-sm font-medium text-gray-700 pl-8">
                    Supplier
                  </label>
                  <CustomDropdown
                    optionsMap={supplierDataMap || {}}
                    isMultiSelect={false}
                    placeholder={"Select Supplier"}
                    getListItemText={(data: any) => data?.supplierName}
                    searchPlaceholder={"Search a supplier"}
                    onSubmit={(supplierIds: any) =>
                      handleSupplierSelection(supplierIds)
                    }
                    preSelectedValues={supplierId}
                    uniqueKey={"mid"}
                    showErrorBox={false}
                    regexCheck={allowEverything}
                  ></CustomDropdown>
                </div>
              )}

              <div className="w-full mt-18">
                <label htmlFor="dateRange" className="block text-sm mb-16 font-medium text-gray-700 pl-8">
                  Date Range
                </label>
                <div className="w-full">
                  <DatePickerWrapper
                    className="w-full"
                    options={optionsDataForFilter}
                    drpdownUniqueKey="value"
                    placeholder="please select your date range"
                    preSelectedDropdownValue={""}
                    getDateRange={onDateRangeSubmission}
                    customStyle="w-full h-45 border-[#E8E8E9] rounded-[8px]"
                  />
                </div>
              </div>

              {activityType === INVENTORY_ACTIVITY_TYPES.post_final_goods_received_note && (
                <div className="w-full">
                  <label htmlFor="Supplier" className="block text-sm font-medium text-gray-700 mt-16 mb-16 pl-8">
                    Modified?
                  </label>
                  <CustomDropdown
                    optionsMap={ACTIVITY_MODIFIED_STATUS}
                    isMultiSelect={false}
                    placeholder={"isEdited"}
                    getListItemText={(data: any) => {
                      return data?.text;
                    }}
                    parentClass={""}
                    dropdownHeading={""}
                    searchPlaceholder={""}
                    onSubmit={(value: any) =>
                      handleIsEdited(value[0])
                    }
                    preSelectedValues={isEdited}
                    uniqueKey="value"
                    showErrorBox={false}
                    isDisabled={false}
                  ></CustomDropdown>
                </div>
              )}

            </div>


            <div className="flex items-center justify-end mt-[24px] sm:mt-[48px] sm:justify-start ">
              <CtaComponent
                ctaText={"Close"}
                ctaType={"secondary-cta"}
                ctaSize={CTA_SIZE.small}
                onCtaClick={onClose}
                customClass={"mr-12"}
              />
              <CtaComponent
                ctaText={"Apply Filter"}
                ctaType={CTA_TYPE.active_cta}
                ctaSize={CTA_SIZE.small}
                onCtaClick={handleApplyFilters}
                customClass={"mr-12"}
              />
            </div>
          </div>

        </div>
      }
    />
  );
};

export default ActivityFilterModal;